// src/services/videoWatchProgress.js
// Client-side helper for flushing on-demand watch progress to /api/track-watch.
// Used by VideoPlayerModal during class-recording playback.
//
// The fetch uses `keepalive: true` so the request survives a tab close /
// page unload — this is what makes the visibilitychange + pagehide flushes
// reliable. (Modern web platform standard, broadly supported.)
//
// See docs/decisions/adr-on-demand-watch-tracking.md.

// Sends a single watch-progress flush. Best-effort — errors are logged,
// not thrown, because most callers fire from cleanup / unload contexts
// where exception handling has nowhere useful to surface.
//
// Required: getToken (Clerk) — auth via Bearer header to keep the same
// pattern as every other authenticated endpoint.
// Required: recordingId — the classRecordings doc id (= muxAssetId).
// Required: watchedSecondsDelta — non-negative number, delta since last flush.
// Optional: classId — denormalized for admin UI grouping.
// Optional: isSessionStart — true on the FIRST flush of a modal-open;
//   server uses this to increment sessionsCount.
//
// Returns: a promise resolving to true on apparent success, false otherwise.
// Note: with `keepalive: true` during page unload, the promise may never
// resolve — the request is queued by the browser and fires fire-and-forget.
// Callers in unload contexts should NOT await this.
export async function flushWatchProgress({ getToken, recordingId, classId, watchedSecondsDelta, isSessionStart }) {
    if (typeof getToken !== 'function') return false;
    if (!recordingId) return false;
    if (typeof watchedSecondsDelta !== 'number' || watchedSecondsDelta < 0) return false;
    try {
        const token = await getToken();
        if (!token) return false;
        const res = await fetch('/api/track-watch', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                recordingId,
                classId: classId || null,
                watchedSecondsDelta: Math.round(watchedSecondsDelta),
                isSessionStart: isSessionStart === true,
            }),
            // keepalive: true — request survives page unload. This is the
            // load-bearing piece that makes visibilitychange/pagehide
            // flushes reliable; without it we'd lose the final delta on
            // every tab close. Browser-imposed body-size limit is ~64KB
            // total per page across all keepalive requests; our payload
            // is well under that.
            keepalive: true,
        });
        return res.ok;
    } catch (e) {
        // Deliberate console.debug — keepalive fetches during unload may
        // appear to "fail" client-side even when the request is queued
        // and processed server-side. Don't pollute Sentry with these.
        if (typeof console !== 'undefined' && console.debug) {
            console.debug('flushWatchProgress non-fatal:', e?.message || e);
        }
        return false;
    }
}
